/**
 * Multi-Agent Node Architecture Types for Ameen Insurance Assistant
 * 
 * This module defines the core types for the node-based agent system
 * where each agent has focused responsibilities, specific tools, and
 * self-managed transitions with guardrails.
 */

import { FunctionDeclaration, LiveConnectConfig, Modality } from "@google/genai";

/**
 * Supported languages in the system
 */
export type SupportedLanguage = 'ar' | 'en' | 'ur';

/**
 * Voice gender options
 */
export type VoiceGender = 'male' | 'female';

/**
 * Loading overlay type - used to show context-appropriate UI (document upload vs vehicle analyze)
 */
export type LoadingType = 'document_upload' | 'vehicle_analyze' | 'api';

/**
 * Flow step states for UI rendering
 */
export type FlowStep =
  | 'idle'
  | 'package_selection'
  | 'national_id_front'
  | 'national_id_back'
  | 'driving_license'
  | 'vehicle_license_front'
  | 'vehicle_license_back'
  | 'underwriting_vehicle_usage'
  | 'underwriting_had_accident'
  | 'underwriting_night_parking'
  | 'underwriting_violations'
  | 'underwriting_tracking_device'
  | 'underwriting_vehicle_financing'
  | 'underwriting_number_of_drivers'
  | 'vehicle_survey'
  | 'policy_survey_options'
  | 'policy_vehicle_upload'
  | 'vehicle_survey_options'
  | 'vehicle_upload'
  | 'policy_confirmation'
  | 'claim_policy_inquiry'
  | 'claim_survey_options'
  | 'claim_vehicle_upload'
  | 'claim_vehicle_survey'
  | 'claim_workshop_options'
  | 'claim_contracted_workshops'
  | 'claim_non_contracted_form'
  | 'claim_number_display';

/**
 * Agent transition target - where an agent can route to
 */
export interface TransitionTarget {
  agentId: string;
  condition?: string; // Human-readable condition description
}

/**
 * Flow state passed to vehicle inspection (policy flow). Only small, typed fields.
 * Matches backend go_to_vehicle_inspection_agent flow_state schema.
 */
export interface PolicyFlowState {
  current_step: number; // 0-5, policy step index
  manufactorYear?: number;
  vehiclevalue?: number;
  isElectricCar?: boolean;
  selected_package_code?: string;
  selected_package_price?: number;
  /** Make as stated by the user in policy Step 1 (e.g. "تويوتا", "Toyota"). Used for chassis verification. */
  vehicleMake?: string;
  /** Model as stated by the user in policy Step 1 (e.g. "كورولا", "Corolla"). Used for chassis verification. */
  vehicleModel?: string;
}

/**
 * Claim flow state (minimal). Matches backend claim go_to_vehicle_inspection_agent flow_state.
 */
export interface ClaimFlowState {
  current_step?: number;
}

/**
 * Data that can be passed between agents during transitions
 */
export interface TransitionData {
  language?: SupportedLanguage;
  voiceGender?: VoiceGender;
  userData?: {
    username: string;
    mobileNumber: string;
  };
  userLocation?: {
    lat: number;
    lng: number;
    address?: string;
  };
  // Ameen-specific flow state
  caller_agent?: string;
  flow_state?: PolicyFlowState | ClaimFlowState;
  damage_description?: string;
  /** Chassis number extracted during vehicle inspection. Forwarded to submit_policy_agent / submit_claim_agent. */
  chassis_number?: string | null;
  /** Make/model comparison result from chassis validation: "match" | "mismatch" | "skipped" | null. */
  make_model_match_result?: string | null;
  selected_package?: { code: string; price: number };
  customer_data?: any;
  survey_images?: { count: number };
  policy_number?: string;
  claim_request_num?: string;
  from_inspection?: boolean;
  /** When true, next agent is router (main menu) and this is a return, not fresh start */
  return_to_menu?: boolean;
  // Flow-specific data
  reportData?: any;
  selectedVehicle?: any;
  renewalResult?: any;
  accidentData?: any;
  accidentResult?: any;
  licenseData?: any;
  searchResults?: any;
  // Custom data for specific flows
  customData?: Record<string, any>;
}

/**
 * A single message in conversation history
 */
export interface ConversationMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  agentId?: string; // Which agent generated this message
}

/**
 * Conversation history carried between agents
 */
export interface ConversationHistory {
  messages: ConversationMessage[];
  summary?: string; // Optional summary for long conversations
  maxMessages?: number; // Limit history size
}

/**
 * Context passed to agents - includes history and transition data
 */
export interface AgentContext {
  conversationHistory: ConversationHistory;
  transitionData: TransitionData;
  previousAgentId?: string;
  transitionReason?: string;
}

/**
 * Guardrail definition for validating transitions
 */
export interface Guardrail {
  id: string;
  description: string;
  /**
   * Validation function that checks if the guardrail condition is met
   * @param context Current agent context
   * @returns true if guardrail passes, false or error message if fails
   */
  validate: (context: AgentContext) => boolean | string;
}

/**
 * Entry guardrails - conditions that must be met to enter an agent
 */
export interface EntryGuardrails {
  required: Guardrail[];
  optional?: Guardrail[]; // Warnings but not blockers
}

/**
 * Exit guardrails - conditions that must be met to leave an agent
 */
export interface ExitGuardrails {
  /**
   * Map of exit conditions to target agents
   * Key is the condition name (e.g., "language_detected", "option_selected")
   * Value is the target agent and validation
   */
  transitions: Map<string, {
    target: TransitionTarget;
    guardrails: Guardrail[];
  }>;
  /**
   * Default fallback if no transition matches
   */
  fallback?: TransitionTarget;
}

/**
 * Tool call result from an agent
 */
export interface ToolCallResult {
  toolName: string;
  args: Record<string, any>;
  result: any;
  triggersTransition?: boolean;
  transitionTo?: string;
  transitionData?: Partial<TransitionData>;
  /**
   * Optional delay before transition:
   * - undefined or 0: Immediate transition
   * - -1: Wait for audio completion (turncomplete event + buffer)
   * - positive number: Fixed delay in milliseconds
   */
  transitionDelayMs?: number;
}

/**
 * UI State Manager Interface - passed to AgentNodes for UI state updates
 */
export interface UIStateManager {
  // Flow step management
  setFlowStep: (step: FlowStep) => void;
  getFlowStep: () => FlowStep;

  // Package selection data
  setCompanies: (companies: any[]) => void;
  getCompanies: () => any[];

  // Contracted workshops data
  setContractedWorkshops: (workshops: any[]) => void;
  getContractedWorkshops: () => any[];

  // Survey images management
  getSurveyImages: () => any[];
  setSurveyImages: (images: any[]) => void;
  getClaimSurveyImages: () => any[];
  setClaimSurveyImages: (images: any[]) => void;

  // Removes a survey image by side from state + sessionStorage (used on validation failure rollback)
  removeSurveyImage?: (side: string) => void;

  // Called by button path after successful per-step validation — agent updates its closure state.
  // This ensures the agent is the single source of truth for sideDescriptions/chassisNumber/etc.
  onButtonCaptureValidated?: (result: {
    side: string;
    damageDescription?: string | null;
    confidenceScore: number;
    chassisNumber?: string | null;
    makeModelMatchResult?: string | null;
  }) => void;

  // Called by button path to get vehicleMake/model for validation.
  // Agent registers this reading from context.transitionData.flow_state (single source of truth).
  getVehicleMakeModel?: () => { make?: string; model?: string };

  /**
   * Stable (ref-backed) per-side damage description store written by the button capture path.
   * Lives in a useRef so it survives UIStateManager re-creations caused by React re-renders.
   * Populated by handleSurveyCaptureClick in Altair.tsx; read by VehicleInspectionAgent at
   * return_to_caller time to recover button-path data that onButtonCaptureValidated may have missed.
   */
  getButtonPathDamages?: () => Record<string, string | null>;
  /** Clears the button-path damage store — called at start_camera_stream to begin a fresh survey. */
  clearButtonPathDamages?: () => void;

  // Customer data management
  getCustomerNationalIdData: () => any;
  setCustomerNationalIdData: (updater: (prev: any) => any) => void;

  // Claim data management
  getClaimPolicyData: () => any;
  setClaimPolicyData: (data: any) => void;

  // Modal display
  showPolicyNumber: (number: string, title: string, message: string, onConfirm: () => void) => void;
  showExtractedDataConfirmation: (
    data: any[],
    title: string,
    documentType: string,
    onConfirm: (editedData?: any[]) => void
  ) => void;

  // Loading overlay (long-running .NET/API: document upload, vehicle analyze, etc.)
  showLoading: (message: string, type?: LoadingType) => void;
  hideLoading: () => void;

  // Camera controls
  onStartCameraStream: () => Promise<boolean>;
  onTakeVehiclePicture: (side: string, flowStep?: FlowStep) => Promise<string | null>;
  onCloseCameraStream: () => void;

  // Session storage helpers
  saveToSessionStorage: (key: string, data: any) => void;
  getFromSessionStorage: (key: string) => any;

  // Client reference (for sending system_ui messages)
  getClient: () => any;
}

/**
 * Agent node definition - the core building block
 */
export interface AgentNode {
  /**
   * Unique identifier for this agent (MUST match backend agent_id exactly)
   */
  id: string;

  /**
   * Human-readable name
   */
  name: string;

  /**
   * Description of what this agent does
   */
  description: string;

  /**
   * Focused system instruction for this agent
   * Only used in LEGACY MODE - in ephemeral token mode, this comes from backend
   * @deprecated Use backend configuration in ephemeral token mode
   */
  systemInstruction?: string;

  /**
   * Tools available to this agent
   * Only used in LEGACY MODE - in ephemeral token mode, this comes from backend
   * @deprecated Use backend configuration in ephemeral token mode
   */
  tools?: FunctionDeclaration[];

  /**
   * Entry guardrails - what conditions must be met to activate this agent
   */
  entryGuardrails: EntryGuardrails;

  /**
   * Exit guardrails - when and where this agent can transition
   */
  exitGuardrails: ExitGuardrails;

  /**
   * Response modality configuration
   */
  responseModality?: Modality[];

  /**
   * Voice configuration for this agent (can differ per agent if needed)
   */
  voiceConfig?: {
    voiceName: string;
  };

  /**
   * Initial prompt sent to the AI when agent activates
   * This triggers the AI to speak first (greeting, menu, instructions, etc.)
   * Without this, the AI waits for user input before responding.
   */
  initialPrompt?: string;

  /**
   * Handler for tool calls specific to this agent
   * Returns updated context and potential transition
   * THIS IS THE SINGLE SOURCE OF TRUTH FOR TOOL HANDLING
   */
  handleToolCall?: (
    toolName: string,
    args: Record<string, any>,
    context: AgentContext,
    uiState?: UIStateManager
  ) => Promise<ToolCallResult>;

  /**
   * Optional callback when agent is activated
   */
  onActivate?: (context: AgentContext) => Promise<void>;

  /**
   * Optional callback when agent is deactivated
   */
  onDeactivate?: (context: AgentContext) => Promise<void>;
}

/**
 * Agent transition event
 */
export interface AgentTransitionEvent {
  fromAgentId: string;
  toAgentId: string;
  reason: string;
  context: AgentContext;
  timestamp: Date;
}

/**
 * Agent manager state
 */
export interface AgentManagerState {
  currentAgentId: string | null;
  context: AgentContext;
  isConnected: boolean;
  isTransitioning: boolean;
  transitionHistory: AgentTransitionEvent[];
  // UI state for Altair component
  flowStep?: FlowStep;
  companies?: any[];
  contractedWorkshops?: any[];
}

/**
 * Configuration for creating an agent's LiveConnectConfig
 */
export interface AgentLiveConfig {
  systemInstruction: string;
  tools: FunctionDeclaration[];
  voiceGender: VoiceGender;
  responseModality?: Modality[];
}

/**
 * Build LiveConnectConfig from agent configuration
 */
export function buildLiveConfig(config: AgentLiveConfig): LiveConnectConfig {
  const voiceName = config.voiceGender === 'female' ? 'Aoede' : 'Charon';

  return {
    responseModalities: config.responseModality || [Modality.AUDIO],
    speechConfig: {
      voiceConfig: { prebuiltVoiceConfig: { voiceName } },
    },
    systemInstruction: {
      parts: [{ text: config.systemInstruction }],
    },
    tools: [{ functionDeclarations: config.tools }],
  };
}

/**
 * Create empty context for initial agent activation
 */
export function createInitialContext(
  userData?: TransitionData['userData'],
  voiceGender: VoiceGender = 'male'
): AgentContext {
  return {
    conversationHistory: {
      messages: [],
      maxMessages: 50,
    },
    transitionData: {
      voiceGender,
      userData,
    },
  };
}

/**
 * Add a message to conversation history
 */
export function addToHistory(
  history: ConversationHistory,
  message: Omit<ConversationMessage, 'timestamp'>
): ConversationHistory {
  const newMessage: ConversationMessage = {
    ...message,
    timestamp: new Date(),
  };

  const messages = [...history.messages, newMessage];

  // Trim if exceeds max
  const maxMessages = history.maxMessages || 50;
  const trimmedMessages = messages.length > maxMessages
    ? messages.slice(-maxMessages)
    : messages;

  return {
    ...history,
    messages: trimmedMessages,
  };
}

/**
 * Get recent history as text for context injection
 */
export function getHistoryText(
  history: ConversationHistory,
  lastN: number = 10
): string {
  const recentMessages = history.messages.slice(-lastN);

  return recentMessages
    .map(msg => `${msg.role}: ${msg.content}`)
    .join('\n');
}
